import { AdvancedMarker, Map, MapCameraChangedEvent, MapCameraProps } from '@vis.gl/react-google-maps';
import { useEffect, useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { ILocation } from '../../interfaces/ILocation';
import { Box, Button, Paper, Typography, Chip, Fade, alpha } from '@mui/material';
import { LocationOn, Info, MapOutlined } from '@mui/icons-material';
import config from '../../config';

interface LocationMapPickerProps {
    latitude: number | undefined;
    longitude: number | undefined;
    setValue?: UseFormSetValue<ILocation>;
    selectedLatitude?: number;
    selectedLongitude?: number;
    selectedPlace?: string;
    readOnly?: boolean;
    lazy?: boolean;
}

const LocationMapPicker: React.FC<LocationMapPickerProps> = ({
    latitude,
    longitude,
    setValue,
    selectedLatitude,
    selectedLongitude,
    selectedPlace,
    readOnly = false,
    lazy,
}) => {
    // When readOnly, default to lazy loading to avoid unnecessary Google Maps API calls
    const isLazy = lazy !== undefined ? lazy : readOnly;
    const [mapRevealed, setMapRevealed] = useState(!isLazy);

    const INITIAL_CAMERA = {
        center: { lat: latitude || 54.0, lng: longitude || -2.0 },
        zoom: latitude && longitude ? 17 : 5
    };
    const [cameraProps, setCameraProps] = useState<MapCameraProps>(INITIAL_CAMERA);
    const [markerCenter, setMarkerCenter] = useState({ lat: latitude || 54.0, lng: longitude || -2.0 });
    const [currentPlace, setCurrentPlace] = useState<string>('');
    const [isPlaceSelected, setIsPlaceSelected] = useState<boolean>(false);

    const handleMarkerDragend = (event: google.maps.MapMouseEvent) => {
        if (readOnly) return;
        const newLat = event.latLng?.lat();
        const newLng = event.latLng?.lng();

        if (newLat !== undefined && newLng !== undefined) {
            setMarkerCenter({ lat: newLat, lng: newLng });
            setCameraProps({ ...cameraProps, center: { lat: newLat, lng: newLng } });
            if (setValue) {
                setValue('latitude', newLat);
                setValue('longitude', newLng);
            }
        }
    };

    const onCameraChangedEvent = (ev: MapCameraChangedEvent) => {
        setCameraProps(ev.detail);
    };

    useEffect(() => {
        if (latitude !== undefined && longitude !== undefined) {
            setCameraProps((prev) => ({
                ...prev,
                center: { lat: latitude, lng: longitude },
                zoom: 17,
            }));
            setMarkerCenter({ lat: latitude, lng: longitude });
        }
        if (selectedPlace) {
            setCurrentPlace(selectedPlace);
            setIsPlaceSelected(true);
        }
    }, [latitude, longitude, selectedPlace]);

    useEffect(() => {
        if (latitude !== undefined && longitude !== undefined) {
            setCameraProps((prev) => ({
                ...prev,
                center: { lat: latitude, lng: longitude },
                zoom: 17,
            }));
            setMarkerCenter({ lat: latitude, lng: longitude });
        }
        if (selectedPlace) {
            setCurrentPlace(selectedPlace);
            setIsPlaceSelected(true);
        }
    }, []);

    return (
        <Box sx={{ position: 'relative' }}>
            {/* Lazy placeholder — only shown before user reveals the map */}
            {!mapRevealed && (
                <Box
                    sx={{
                        position: 'relative',
                        borderRadius: 3,
                        overflow: 'hidden',
                        border: `2px solid ${alpha('#493193', 0.15)}`,
                        boxShadow: `0 4px 20px ${alpha('#493193', 0.08)}`,
                        height: '400px',
                        cursor: 'pointer',
                        // Simulated map background using layered CSS gradients
                        background: `
                            linear-gradient(rgba(240,242,235,0.92), rgba(240,242,235,0.92)),
                            repeating-linear-gradient(0deg, transparent, transparent 48px, ${alpha('#b0bec5', 0.35)} 48px, ${alpha('#b0bec5', 0.35)} 50px),
                            repeating-linear-gradient(90deg, transparent, transparent 48px, ${alpha('#b0bec5', 0.35)} 48px, ${alpha('#b0bec5', 0.35)} 50px),
                            repeating-linear-gradient(0deg, transparent, transparent 12px, ${alpha('#cfd8dc', 0.2)} 12px, ${alpha('#cfd8dc', 0.2)} 13px),
                            repeating-linear-gradient(90deg, transparent, transparent 12px, ${alpha('#cfd8dc', 0.2)} 12px, ${alpha('#cfd8dc', 0.2)} 13px)
                        `,
                    }}
                    onClick={() => setMapRevealed(true)}
                    role="button"
                    aria-label="Show map"
                >
                    {/* Road-like accent shapes */}
                    <Box sx={{
                        position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none',
                    }}>
                        <Box sx={{ position: 'absolute', top: '30%', left: 0, right: 0, height: 10, bgcolor: alpha('#fff', 0.6), borderRadius: 2 }} />
                        <Box sx={{ position: 'absolute', top: '60%', left: 0, right: '30%', height: 6, bgcolor: alpha('#fff', 0.5), borderRadius: 2 }} />
                        <Box sx={{ position: 'absolute', top: '20%', bottom: 0, left: '40%', width: 8, bgcolor: alpha('#fff', 0.55), borderRadius: 2 }} />
                        <Box sx={{ position: 'absolute', top: 0, bottom: '20%', left: '70%', width: 5, bgcolor: alpha('#fff', 0.45), borderRadius: 2 }} />
                        {/* Green area suggestions */}
                        <Box sx={{ position: 'absolute', top: '10%', right: '10%', width: 90, height: 60, bgcolor: alpha('#c8e6c9', 0.6), borderRadius: 3 }} />
                        <Box sx={{ position: 'absolute', bottom: '15%', left: '8%', width: 60, height: 40, bgcolor: alpha('#c8e6c9', 0.5), borderRadius: 2 }} />
                    </Box>

                    {/* Centred "Show map" button */}
                    <Box
                        sx={{
                            position: 'absolute', inset: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                    >
                        <Button
                            variant="outlined"
                            startIcon={<MapOutlined />}
                            onClick={(e) => { e.stopPropagation(); setMapRevealed(true); }}
                            sx={{
                                bgcolor: 'rgba(255,255,255,0.92)',
                                backdropFilter: 'blur(6px)',
                                borderColor: alpha('#493193', 0.4),
                                color: '#2d1f5e',
                                fontWeight: 600,
                                fontSize: '0.95rem',
                                textTransform: 'none',
                                borderRadius: '8px',
                                px: 3,
                                py: 1,
                                boxShadow: `0 2px 12px ${alpha('#493193', 0.15)}`,
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,1)',
                                    borderColor: '#493193',
                                    boxShadow: `0 4px 16px ${alpha('#493193', 0.25)}`,
                                },
                            }}
                        >
                            Show map
                        </Button>
                    </Box>
                </Box>
            )}

            {/* Actual Google Map — only mounted after the user reveals it */}
            {mapRevealed && (
                <Box
                    sx={{
                        position: 'relative',
                        borderRadius: 3,
                        overflow: 'hidden',
                        border: `2px solid ${alpha('#493193', 0.15)}`,
                        boxShadow: `0 4px 20px ${alpha('#493193', 0.08)}`,
                    }}
                >
                    <Map
                        style={{ width: '100%', height: '400px' }}
                        defaultZoom={17}
                        gestureHandling={'greedy'}
                        disableDefaultUI={readOnly}
                        fullscreenControl={true}
                        mapTypeControl={!readOnly}
                        zoomControl={true}
                        mapId={config.googleMapsMapId}
                        {...cameraProps}
                        onCameraChanged={onCameraChangedEvent}
                    >
                        <AdvancedMarker
                            position={markerCenter}
                            draggable={!readOnly}
                            onDragEnd={handleMarkerDragend}
                        />
                    </Map>

                    <Fade in={isPlaceSelected}>
                        <Paper
                            elevation={2}
                            sx={{
                                position: 'absolute',
                                bottom: 16,
                                left: 16,
                                p: 1.5,
                                px: 2.5,
                                borderRadius: '50px',
                                display: 'flex',
                                alignItems: 'center',
                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                backdropFilter: 'blur(8px)',
                                boxShadow: `0 4px 12px ${alpha('#493193', 0.15)}`,
                                border: `1px solid ${alpha('#493193', 0.1)}`,
                            }}
                        >
                            <LocationOn sx={{ mr: 1, color: '#493193' }} />
                            <Typography variant="body2" fontWeight={600} sx={{ color: '#493193' }}>
                                {currentPlace}
                            </Typography>
                        </Paper>
                    </Fade>
                    {!readOnly && (
                        <Chip
                            icon={<Info fontSize="small" />}
                            label="Drag marker to adjust precise location"
                            size="small"
                            sx={{
                                position: 'absolute',
                                top: 16,
                                right: 60,
                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                backdropFilter: 'blur(8px)',
                                borderRadius: '50px',
                                fontWeight: 500,
                                border: `1px solid ${alpha('#493193', 0.2)}`,
                                color: '#493193',
                                boxShadow: `0 2px 8px ${alpha('#493193', 0.1)}`,
                                '& .MuiChip-icon': {
                                    color: '#493193',
                                },
                            }}
                        />
                    )}
                </Box>
            )}
            {!readOnly && (
                <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        <strong>Lat:</strong> {markerCenter.lat.toFixed(6)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        <strong>Lng:</strong> {markerCenter.lng.toFixed(6)}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default LocationMapPicker;